/**
 * Pull down to refresh (mobile M3) — on touch screens only.
 *
 * Starts only when the page is scrolled to the very top, nothing is open over
 * it, and the finger moves mostly downward; anything else is an ordinary
 * scroll. Past `TRIGGER` px the release runs `onRefresh` and the indicator
 * stays until it settles. A mouse never triggers it: the desktop app has no
 * pull gesture to gain and a stray drag must not start a sync.
 */

import * as React from "react";

import { overlayOpen } from "@/lib/back";

/** Drag distance (px, after resistance) that commits a refresh. */
export const TRIGGER = 72;

/** Rubber-band resistance: the indicator moves slower than the finger. */
export function resist(dy: number): number {
  return Math.max(0, Math.min(120, dy * 0.5));
}

export type PullState = { pull: number; refreshing: boolean };

export function usePullToRefresh(onRefresh: () => Promise<unknown>, enabled = true): PullState {
  const [state, setState] = React.useState<PullState>({ pull: 0, refreshing: false });
  const start = React.useRef<{ x: number; y: number } | null>(null);
  const pull = React.useRef(0);
  const busy = React.useRef(false);
  const refresh = React.useRef(onRefresh);
  refresh.current = onRefresh;

  React.useEffect(() => {
    if (!enabled) return;

    const onStart = (e: TouchEvent) => {
      if (busy.current || window.scrollY > 0 || overlayOpen() || e.touches.length !== 1) {
        start.current = null;
        return;
      }
      const t = e.touches[0]!;
      start.current = { x: t.clientX, y: t.clientY };
      pull.current = 0;
    };

    const onMove = (e: TouchEvent) => {
      if (!start.current) return;
      const t = e.touches[0]!;
      const dy = t.clientY - start.current.y;
      const dx = Math.abs(t.clientX - start.current.x);
      // Sideways or upward: a swipe through a chip row or a scroll, not a pull.
      if (dy <= 0 || dx > dy) {
        if (pull.current === 0) start.current = null;
        return;
      }
      pull.current = resist(dy);
      setState({ pull: pull.current, refreshing: false });
    };

    const onEnd = () => {
      if (!start.current) return;
      start.current = null;
      const committed = pull.current >= TRIGGER;
      pull.current = 0;
      if (!committed) {
        setState({ pull: 0, refreshing: false });
        return;
      }
      busy.current = true;
      setState({ pull: TRIGGER, refreshing: true });
      void refresh.current().finally(() => {
        busy.current = false;
        setState({ pull: 0, refreshing: false });
      });
    };

    window.addEventListener("touchstart", onStart, { passive: true });
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onEnd);
    window.addEventListener("touchcancel", onEnd);
    return () => {
      window.removeEventListener("touchstart", onStart);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
      window.removeEventListener("touchcancel", onEnd);
    };
  }, [enabled]);

  return state;
}
